import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export function InstallAppBanner() {
  const { i18n } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [showIosGuide, setShowIosGuide] = useState(false);

  useEffect(() => {
    // 1. Check if already installed / running standalone
    const isStandalone =
      // matchMedia is absent from jsdom and from a few embedded webviews.
      window.matchMedia?.('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;

    if (isStandalone) return;

    // 2. Check if recently dismissed (within 7 days)
    const dismissedAt = localStorage.getItem('myscaw_pwa_dismissed');
    if (dismissedAt) {
      const diff = Date.now() - Number(dismissedAt);
      if (diff < 7 * 24 * 60 * 60 * 1000) {
        return;
      }
    }

    // 3. Detect iOS device in Safari
    const ua = window.navigator.userAgent.toLowerCase();
    const isIosDevice = /iphone|ipad|ipod/.test(ua) && !(window as any).MSStream;
    const isSafari = /safari/.test(ua) && !/chrome|crios|fxios|edgios/.test(ua);

    if (isIosDevice && isSafari) {
      setIsIos(true);
      // Show iOS banner after a brief graceful delay (2 seconds)
      const timer = setTimeout(() => setShowBanner(true), 2000);
      return () => clearTimeout(timer);
    }

    // 4. Android & Desktop Chrome/Edge beforeinstallprompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIos) {
      setShowIosGuide(true);
      return;
    }

    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowBanner(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setShowIosGuide(false);
    localStorage.setItem('myscaw_pwa_dismissed', Date.now().toString());
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Floating Bottom App Install Banner */}
      <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:w-96 z-40 animate-in fade-in slide-in-from-bottom-4 duration-300">
        <div className="bg-surface-container-lowest border border-outline-variant/60 rounded-3xl p-4 shadow-xl flex items-center gap-3.5 backdrop-blur-md">
          {/* App Icon */}
          <div className="w-11 h-11 rounded-2xl bg-primary text-on-primary flex items-center justify-center shrink-0 shadow-xs overflow-hidden">
            <img src="/apple-touch-icon.png" alt="MySCAW" className="w-full h-full object-cover" />
          </div>

          {/* Text Content */}
          <div className="flex-1 min-w-0">
            <h4 className="font-bold text-xs sm:text-sm text-primary leading-tight truncate">
              {i18n.language === 'en' ? 'Install MySCAW' : 'Installer MySCAW'}
            </h4>
            <p className="text-[11px] text-on-surface-variant leading-snug line-clamp-1">
              {i18n.language === 'en'
                ? 'Add to home screen for 1-tap access'
                : 'Ajouter sur votre écran d’accueil'}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              onClick={handleInstallClick}
              className="px-3.5 py-2 rounded-full bg-primary text-on-primary hover:bg-primary/90 text-xs font-bold transition-all shadow-xs active:scale-95 min-h-[36px]"
            >
              {i18n.language === 'en' ? 'Install' : 'Installer'}
            </button>
            <button
              onClick={handleDismiss}
              className="p-1.5 rounded-full hover:bg-surface-container text-on-surface-variant transition-colors"
              title={i18n.language === 'en' ? 'Close' : 'Fermer'}
            >
              <span className="material-symbols-outlined text-[18px]">close</span>
            </button>
          </div>
        </div>
      </div>

      {/* iOS Step-by-Step Installation Modal Guide */}
      {showIosGuide && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-200">
          <div className="bg-surface-container-lowest rounded-3xl max-w-md w-full p-5 sm:p-6 shadow-2xl border border-outline-variant/30 space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-outline-variant/30">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-primary text-on-primary flex items-center justify-center overflow-hidden">
                  <img
                    src="/apple-touch-icon.png"
                    alt="MySCAW"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h3 className="font-bold text-sm sm:text-base text-primary">
                    {i18n.language === 'en'
                      ? 'Install on iPhone / iPad'
                      : 'Installer sur iPhone / iPad'}
                  </h3>
                  <p className="text-[11px] text-on-surface-variant">Safari iOS</p>
                </div>
              </div>
              <button
                onClick={() => setShowIosGuide(false)}
                className="p-1.5 rounded-full hover:bg-surface-container text-on-surface-variant"
              >
                <span className="material-symbols-outlined text-[20px]">close</span>
              </button>
            </div>

            {/* Steps Visual List */}
            <div className="space-y-3.5 text-xs">
              <div className="flex items-start gap-3 p-3 rounded-2xl bg-surface-container-low border border-outline-variant/20">
                <span className="w-6 h-6 rounded-full bg-primary text-on-primary flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                  1
                </span>
                <div className="flex-1">
                  <p className="font-bold text-on-surface">
                    {i18n.language === 'en'
                      ? 'Tap the Share button'
                      : 'Appuyez sur le bouton Partager'}
                  </p>
                  <p className="text-on-surface-variant text-[11px] mt-0.5">
                    {i18n.language === 'en'
                      ? 'Located at the bottom of the Safari toolbar (square with upward arrow ⎋)'
                      : 'Situé en bas dans la barre de Safari (icône rectangle avec flèche vers le haut ⎋)'}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 rounded-2xl bg-surface-container-low border border-outline-variant/20">
                <span className="w-6 h-6 rounded-full bg-primary text-on-primary flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                  2
                </span>
                <div className="flex-1">
                  <p className="font-bold text-on-surface">
                    {i18n.language === 'en'
                      ? 'Select "Add to Home Screen"'
                      : 'Choisissez « Sur l’écran d’accueil »'}
                  </p>
                  <p className="text-on-surface-variant text-[11px] mt-0.5">
                    {i18n.language === 'en'
                      ? 'Scroll down in the action list and tap the (+) icon'
                      : 'Faites défiler la liste vers le bas et touchez l’icône (+)'}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 rounded-2xl bg-surface-container-low border border-outline-variant/20">
                <span className="w-6 h-6 rounded-full bg-primary text-on-primary flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                  3
                </span>
                <div className="flex-1">
                  <p className="font-bold text-on-surface">
                    {i18n.language === 'en' ? 'Tap "Add"' : 'Appuyez sur « Ajouter »'}
                  </p>
                  <p className="text-on-surface-variant text-[11px] mt-0.5">
                    {i18n.language === 'en'
                      ? 'In the top right corner. MySCAW is now installed like a native app!'
                      : 'En haut à droite. L’icône MySCAW apparaîtra sur votre écran d’accueil !'}
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-2 flex justify-end">
              <button
                onClick={() => setShowIosGuide(false)}
                className="w-full sm:w-auto px-5 py-2.5 rounded-full bg-primary text-on-primary font-bold text-xs min-h-[44px]"
              >
                {i18n.language === 'en' ? 'Got it' : 'J’ai compris'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
